/**
 * TrustedBrandsMarquee — Phase 18.15.5
 *
 * Fetches active brands from /api/website/trusted-brands and renders
 * them as a smooth, infinite horizontal scroll (marquee). Inspired by
 * the moving country cards on tracking.5999cargo.com.
 *
 * UX rules:
 *  - Smooth continuous animation (CSS keyframes, 45s loop).
 *  - Track is duplicated in JSX so the loop is seamless.
 *  - Hover pauses the scroll.
 *  - Respects prefers-reduced-motion.
 *  - If no brands are returned by the API, the whole section hides
 *    so we never show an empty strip.
 *  - Logo URLs that start with /api/ are prefixed with BACKEND_URL.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveLogo(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url}`;
}

export default function TrustedBrandsMarquee() {
  const [brands, setBrands] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [header, setHeader] = useState({
    eyebrow: "Vertrouwd door duizenden klanten en bedrijven",
    subtitle: "Klanten gebruiken 5999Cargo voor aankopen, pakketten, verhuisgoederen en zakelijke zendingen vanuit Nederland naar de Caribbean.",
  });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/website/trusted-brands`, { timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data?.brands) ? res.data.brands : [];
        setBrands(list);
      })
      .catch(() => setBrands([]))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // Pull section header (eyebrow + subtitle) from homepage settings so
  // admins can rename/rephrase it from the backoffice.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/website/homepage-settings`, { timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        const tb = res?.data?.sections?.trusted_brands_header;
        if (tb && typeof tb === "object") {
          setHeader((prev) => ({ ...prev, ...tb }));
        }
      })
      .catch(() => { /* keep defaults */ });
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide the whole section until we have at least one brand to show —
  // we'd rather skip a placeholder than ship a sad empty strip.
  if (!loaded || brands.length === 0) {
    return null;
  }

  // Duplicate the array for the seamless infinite loop.
  const track = [...brands, ...brands];

  return (
    <section
      className="bg-white py-16 lg:py-20 border-y border-slate-100"
      data-testid="home-trust-strip"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="text-[11px] uppercase tracking-[0.22em] text-[#1a5193] font-bold">
            {header.eyebrow}
          </span>
          {header.subtitle ? (
            <p className="mt-3 text-[#514e4e] text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              {header.subtitle}
            </p>
          ) : null}
        </div>

        <div className="marquee-mask overflow-hidden">
          <div
            className="marquee-track"
            data-testid="trusted-brands-track"
            aria-label="Trusted brands"
          >
            {track.map((b, idx) => (
              <BrandTile key={`${b.id || b.brand_name}-${idx}`} brand={b} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BrandTile({ brand }) {
  const logoSrc = resolveLogo(brand.logo_url);
  const inner = (
    <div
      className="flex-shrink-0 flex items-center justify-center h-20 w-44 sm:w-48 rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_18px_-12px_rgba(8,44,80,0.18)] hover:shadow-[0_12px_24px_-12px_rgba(8,44,80,0.28)] transition-all"
      data-testid={`trusted-brand-${(brand.brand_name || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      {logoSrc ? (
        <img
          src={logoSrc}
          alt={brand.alt_text || brand.brand_name || ""}
          className="max-h-10 max-w-[80%] object-contain opacity-80 hover:opacity-100 transition-opacity"
          loading="lazy"
        />
      ) : (
        <span className="text-base font-bold tracking-tight text-[#082c50]/70">
          {brand.brand_name}
        </span>
      )}
    </div>
  );

  if (brand.link_url) {
    return (
      <a
        href={brand.link_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block"
        aria-label={brand.brand_name}
      >
        {inner}
      </a>
    );
  }
  return inner;
}
